"use client";
import React, { useState, useRef, useEffect } from "react";
import dayjs from "dayjs";
import IMAGES from "@/data/assetData";

import { usefetchDataFromApi } from "@/utils/api";
import { useDispatch, useSelector } from "react-redux";
import { bumpCartVersion } from "@/redux/order/orderSlice";
import { setEmptyCartData, getEndDate } from "@/redux/search/searchSlice";
import Image from "next/image";
import { trackAddToCart, toGa4Item } from "@/utils/analytics";
import { formatDuration } from "@/utils/formatDuration";
import { getRentalDayBilling, RentalDayNote } from "@/components/ui/RentalDayNote";
import { formatTime12h } from "@/data/staticMethod";
import Rupee from "@/components/ui/Rupee";

const DURATION_TABS_BASE = [
  { key: "daily", label: "Daily", days: 1, rateField: "wdDaily", freeKmField: "freeKm" },
  { key: "weekly", label: "Weekly", days: 7, rateField: "weekly", freeKmField: "weeklyFreeKm" },
  { key: "fortnight", label: "2 Weeks", days: 14, rateField: "fortnight", freeKmField: "fortnightFreeKm" },
  { key: "twentyOneDays", label: "3 Weeks", days: 21, rateField: "twentyOneDays", freeKmField: "twentyOneDaysFreeKm" },
  { key: "monthly", label: "Monthly", days: 30, rateField: "monthly", freeKmField: "monthlyFreeKm" },
];

const VehicleCard = ({ openModal, cardData, sDateTime, eDateTime, onDurationChange }) => {
  const dispatch = useDispatch();
  const [selectLocatn, setSelectLocatn] = useState("");
  const searchSelector = useSelector((state) => state.search);
  const [locationValitn, setLocationValitn] = useState(false);
  const [btnDisable, setBtnDisable] = useState(false);
  const [addError, setAddError] = useState("");
  const [vId, setVId] = useState();
  const [imgLoaded, setImgLoaded] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const locRef = useRef(null);

  // Default pickup = the only location, or the cheapest (lowest searched total),
  // so the primary CTA is never a dead tap. Derived (not state) to avoid a flash
  // of the empty placeholder and an extra render. An explicit choice wins.
  const defaultLocationKey = React.useMemo(() => {
    const locs = cardData?.otherlocation;
    if (Array.isArray(locs) && locs.length > 0) {
      return locs.reduce((best, loc) =>
        (loc?.total ?? Infinity) < (best?.total ?? Infinity) ? loc : best
      , locs[0])?.key;
    }
    return cardData?.key;
  }, [cardData]);

  const activeLocationKey = selectLocatn || defaultLocationKey || "";

  // Normalized pickup options for the custom dropdown (name / price / partial flag).
  const locationOptions = React.useMemo(() => {
    if (Array.isArray(cardData?.otherlocation)) {
      return cardData.otherlocation.map((l) => ({ key: l.key, name: l.name, total: l.total, partialStatus: l.partialStatus, distanceKm: l.distanceKm ?? null }));
    }
    if (cardData?.location) {
      return [{ key: cardData.key, name: cardData.location.name, total: cardData?.rent?.total, partialStatus: cardData?.vehicle?.partialStatus, distanceKm: cardData?.location?.distanceKm ?? null }];
    }
    return [];
  }, [cardData]);

  const selectedLocation = locationOptions.find((o) => o.key === activeLocationKey) || null;

  // Close the pickup dropdown on outside-click / Escape.
  useEffect(() => {
    if (!locOpen) return;
    const onDown = (e) => { if (locRef.current && !locRef.current.contains(e.target)) setLocOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setLocOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [locOpen]);

  const selectedItem = React.useMemo(() => {
    if (!activeLocationKey) return null;
    return cardData?.otherlocation?.find((item) => item.key === activeLocationKey) || null;
  }, [cardData, activeLocationKey]);

  const rate = selectedItem?.rate || cardData?.rate;
  const deposit = rate?.deposit;

  // Selected date range price (what the user actually searched for)
  const searchedPrice = selectedItem ? selectedItem.total : cardData?.rent?.total;
  const searchedFreeKm = selectedItem ? selectedItem.freeKm : cardData?.rent?.freeKm;

  const billedLabel = React.useMemo(() => {
    const { startDateTime, endDateTime } = searchSelector;
    if (!startDateTime?.date || !endDateTime?.date) return null;
    return getRentalDayBilling(
      `${startDateTime.date} ${startDateTime.time}`,
      `${endDateTime.date} ${endDateTime.time}`,
      selectedItem || cardData?.rent
    );
  }, [selectedItem, cardData, searchSelector]);

  // Unavailability line from the API's real data: a concrete next-free time,
  // or the location's actual open hours — generic copy only when neither exists.
  const unavailableMessage = React.useMemo(() => {
    const info = cardData?.unavailable;
    if (info?.availableFrom) {
      const from = dayjs(info.availableFrom);
      if (from.isValid()) {
        const datePart = from.year() === dayjs().year() ? from.format("D MMM") : from.format("D MMM YY");
        return `Not available for the selected time. Next available from ${datePart}, ${from.format("h:mm A")}.`;
      }
    }
    if (info?.reason === "timings" && info?.open && info?.close) {
      return `Location Closed. Pickup available between ${formatTime12h(info.open)} to ${formatTime12h(info.close)}.`;
    }
    return "Not available for the selected time. Try a different date or time.";
  }, [cardData]);

  // Trip count — always the aggregate across locations (model popularity), so
  // auto-selecting a single pickup doesn't shrink the social-proof number.
  const totalTrips = React.useMemo(() => {
    if (cardData?.otherlocation) {
      return cardData.otherlocation.reduce((sum, loc) => sum + (loc.bookingCount ?? 0), 0);
    }
    return cardData?.bookingCount ?? 0;
  }, [cardData]);

  // Swap daily rate field based on whether the start date is a weekend
  const DURATION_TABS = React.useMemo(() => {
    const startDate = searchSelector.startDateTime?.date;
    if (!startDate) return DURATION_TABS_BASE;
    const dayOfWeek = dayjs(startDate).day(); // 0=Sun, 1=Mon, ... 5=Fri, 6=Sat
    const isWeekend = dayOfWeek === 0 || dayOfWeek >= 5; // Fri(5), Sat(6), Sun(0)
    if (!isWeekend) return DURATION_TABS_BASE;
    return DURATION_TABS_BASE.map((tab) =>
      tab.key === "daily" ? { ...tab, rateField: "weDaily" } : tab
    );
  }, [searchSelector.startDateTime?.date]);

  // Determine the currently active duration based on the actual searched dates
  const searchedDays = React.useMemo(() => {
    const { startDateTime, endDateTime } = searchSelector;
    if (!startDateTime?.date || !endDateTime?.date) return 0;
    const start = dayjs(`${startDateTime.date} ${startDateTime.time}`);
    const end = dayjs(`${endDateTime.date} ${endDateTime.time}`);
    return end.diff(start, "day");
  }, [searchSelector]);

  const activeDuration = React.useMemo(() => {
    const match = DURATION_TABS.find((t) => t.days === searchedDays);
    return match ? match.key : null;
  }, [searchedDays]);

  // Build available duration tabs (only show tabs that have a rate > 0)
  const availableTabs = React.useMemo(() => {
    if (!rate) return [];
    return DURATION_TABS.filter((tab) => {
      const val = rate[tab.rateField];
      return val !== undefined && val !== null && val > 0;
    });
  }, [rate]);

  // Find the tab with the lowest per-day rate
  const bestValueKey = React.useMemo(() => {
    if (availableTabs.length < 2 || !rate) return null;
    let bestKey = null;
    let lowestPerDay = Infinity;
    availableTabs.forEach((tab) => {
      const tabRate = rate[tab.rateField];
      if (tabRate > 0) {
        const perDay = tabRate / tab.days;
        if (perDay < lowestPerDay) {
          lowestPerDay = perDay;
          bestKey = tab.key;
        }
      }
    });
    return bestKey;
  }, [availableTabs, rate]);

  // Handle duration tab click — update end date in Redux, triggers global re-search
  const handleDurationChange = (tab) => {
    if (activeDuration === tab.key) return; // already selected
    const { startDateTime } = searchSelector;
    if (!startDateTime?.date || !startDateTime?.time) return;
    const newEnd = dayjs(`${startDateTime.date} ${startDateTime.time}`).add(tab.days, "day");
    dispatch(getEndDate({
      date: newEnd.format("YYYY-MM-DD"),
      time: startDateTime.time,
    }));
    onDurationChange?.(newEnd.format("DD MMM YYYY"), newEnd.format("h:mm A"));
  };

  const computedAllPartialStatus = React.useMemo(() => {
    if (selectedItem) return selectedItem?.partialStatus ?? 0;
    if (cardData?.otherlocation !== undefined) {
      for (const [, value] of Object.entries(cardData.otherlocation)) {
        if (value?.partialStatus === 1) return 1;
      }
      return 0;
    }
    return cardData?.vehicle?.partialStatus ?? 0;
  }, [selectedItem, cardData]);

  const handleAddToCart = async (id) => {
    const locKey = activeLocationKey;
    if (!locKey) {
      setLocationValitn(true);
      return;
    }
    setVId(id);
    setAddError("");
    let startDTime = `${searchSelector.startDateTime.date} ${searchSelector.startDateTime.time}`;
    let endDTime = `${searchSelector.endDateTime.date} ${searchSelector.endDateTime.time}`;
    setBtnDisable(true);
    usefetchDataFromApi({
      url: "cart/update",
      method: "POST",
      data: {
        key: locKey,
        search: {
          types: searchSelector.vehicleType,
          start: startDTime,
          end: endDTime,
          cityId: searchSelector.city.id,
          longRentStatus:
            searchSelector?.searchType == "/monthly-bike-rental" ? 1 : 0,
          platform: 3,
        },
        platform: 3,
        action: "add",
      },
    }).then((res) => {
      if (res.success) {
        setBtnDisable(!btnDisable);
        dispatch(bumpCartVersion());
        dispatch(setEmptyCartData(false));
        trackAddToCart(toGa4Item(cardData, searchedPrice), searchedPrice);
        handleMiniCart(id, locKey);
      } else {
        setBtnDisable(false);
        setAddError(res.message || "Could not add this vehicle. Please try again.");
      }
    });
  };

  const handleMiniCart = (vehicleId, locKey = activeLocationKey) => {
    setBtnDisable(!btnDisable);
    let startDTime = `${searchSelector.startDateTime.date} ${searchSelector.startDateTime.time}`;
    let endDTime = `${searchSelector.endDateTime.date} ${searchSelector.endDateTime.time}`;
    usefetchDataFromApi({
      url: "cart/minicart",
      method: "POST",
      data: {
        key: locKey,
        types: searchSelector.vehicleType,
        search: {
          types: searchSelector.vehicleType,
          cityId: searchSelector.city.id,
          start: startDTime,
          end: endDTime,
          longRentStatus:
            searchSelector?.searchType == "/monthly-bike-rental" ? 1 : 0,
          platform: 3,
        },
        vehicleId: vehicleId,
        cityId: searchSelector.city.id,
        platform: 3,
      },
    }).then((res) => {
      setBtnDisable(false);
      openModal(res.data?.data, locKey);
    });
  };

  return (
    <div className={`bg-white rounded-card border border-gray-200 shadow-card-sm p-3.5 w-full relative ${locOpen ? 'z-30' : ''}`}>
      {/* Header: Vehicle Name + Specs. Wrap to a 2nd line (don't cut off) and
          reserve a right gutter so both lines stay clear of the absolutely-
          positioned trust badges; ellipsis only past 2 lines. */}
      <div className="text-base font-semibold text-gray-900 leading-[1.2] tracking-tight line-clamp-2 pr-[112px]">
        {cardData?.vehicle?.company} {cardData?.vehicle?.name}
      </div>
      {(cardData?.vehicle?.fuelType || cardData?.vehicle?.transmissionType) && (
        <div className="flex items-center gap-1.5 mt-0.5">
          {cardData.vehicle.fuelType && (
            <span className="text-xs text-gray-400 font-medium">{cardData.vehicle.fuelType}</span>
          )}
          {cardData.vehicle.fuelType && cardData.vehicle.transmissionType && (
            <span className="text-[10px] text-gray-300">•</span>
          )}
          {cardData.vehicle.transmissionType && (
            <span className="text-xs text-gray-400 font-medium">{cardData.vehicle.transmissionType}</span>
          )}
        </div>
      )}

      {/* Ribbon Badges — right edge */}
      <div className="absolute right-0 top-3.5 flex flex-col items-end gap-1 z-[1]">
        {computedAllPartialStatus === 1 && (
          <span className="inline-flex items-center gap-1 pl-2.5 pr-2 py-1 text-[10px] font-bold leading-none whitespace-nowrap bg-emerald-50 text-emerald-600 rounded-l-full shadow-sm">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
            Partial Payment
          </span>
        )}
        {deposit == 0 && (
          <span className="inline-flex items-center gap-1 pl-2.5 pr-2 py-1 text-[10px] font-bold leading-none whitespace-nowrap bg-blue-50 text-blue-500 rounded-l-full shadow-sm">
            <Image src={IMAGES.zerodeposit_icon} width={10} height={10} alt="Zero Deposit" />
            Zero Deposit
          </span>
        )}
        {(cardData.vehicle?.type == 5 || cardData.vehicle?.type == 6) && (
          <span className="inline-flex items-center gap-1 pl-2.5 pr-2 py-1 text-[10px] font-bold leading-none whitespace-nowrap bg-emerald-50 text-emerald-600 rounded-l-full shadow-sm">
            <Image src={IMAGES.EcoIcon} width={10} height={10} alt="Eco" />
            Eco - friendly
          </span>
        )}
        {totalTrips > 0 && (
          <span className="inline-flex items-center gap-1 pl-2.5 pr-2 py-1 text-[10px] font-bold leading-none whitespace-nowrap bg-amber-50 text-amber-700 rounded-l-full shadow-sm">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z"/></svg>
            {totalTrips} {totalTrips == 1 ? 'Trip' : 'Trips'}
          </span>
        )}
      </div>

      {/* Vehicle Image with shimmer placeholder */}
      <div className="h-28 w-full mt-2 rounded-xl overflow-hidden bg-white flex items-center justify-center relative">
        {!imgLoaded && (
          <div className="absolute inset-0 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-[length:200%_100%] animate-shimmer rounded-xl" />
        )}
        <Image
          src={cardData.vehicle.imageUrl || '/images/img_notfound.png'}
          alt={`${cardData.vehicle.company} ${cardData.vehicle.name}`}
          width={280}
          height={200}
          loading="lazy"
          unoptimized
          className={`max-h-full max-w-full w-auto h-auto object-contain scale-[1.2] relative transition-opacity duration-300 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
          style={{ width: 'auto', height: 'auto' }}
          onLoad={() => setImgLoaded(true)}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = '/images/img_notfound.png';
            setImgLoaded(true);
          }}
        />
      </div>

      {cardData?.vehicleStatus === "available" ? (
        <>
          {/* Design B: no deposit here, and the total price moves to a footer row
              next to the CTA (below the pickup dropdown). */}

          {/* Duration Rate Tabs — clicking changes the search end date globally */}
          {availableTabs.length > 0 && (
            <div className="bg-gray-50 rounded-xl p-2 mt-3 mb-3">
              <p className="text-[11px] font-medium text-gray-400 mb-1.5 px-0.5">Quick Duration</p>
              <div className="flex bg-gray-200/60 rounded-lg p-0.5 gap-0.5">
                {availableTabs.map((tab) => {
                  const isActive = activeDuration === tab.key;
                  const isBestValue = bestValueKey === tab.key;
                  const tabRate = rate?.[tab.rateField];
                  const tabPerDay = tabRate ? Math.round(tabRate / tab.days) : null;
                  return (
                    <div key={tab.key} className="flex-1 flex flex-col items-center relative">
                      {isBestValue && !isActive && (
                        <span className="absolute -top-2.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full leading-none whitespace-nowrap z-[2]">
                          Best Value
                        </span>
                      )}
                      <button
                        onClick={() => handleDurationChange(tab)}
                        className={`w-full py-3 px-0.5 rounded-lg text-center transition-all duration-200 flex flex-col items-center gap-1 ${
                          isActive
                            ? "bg-primary text-white shadow-sm"
                            : isBestValue
                              ? "bg-emerald-50/80 text-emerald-700 ring-1 ring-emerald-200"
                              : "bg-transparent text-gray-500 hover:text-gray-700 hover:bg-white/60"
                        }`}
                      >
                        <span className="text-xs lg:text-[11px] font-bold leading-none whitespace-nowrap">{tab.label}</span>
                        {tabRate > 0 && (
                          <span className={`text-[11px] lg:text-[10px] leading-none whitespace-nowrap ${isActive ? "text-white/80" : isBestValue ? "text-emerald-500" : "text-gray-400"}`}>
                            <Rupee />{tab.days === 1 ? tabRate : tabPerDay}/d
                          </span>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pickup is pre-selected (cheapest/only). With more than one option,
              hint how many others they can switch to; a single-location vehicle
              has nothing to choose, so the line is omitted. */}
          {(cardData?.otherlocation?.length ?? 1) > 1 && (
            <p className="text-xs text-gray-500 mb-1 px-0.5">
              📍 Available at <span className="font-semibold text-gray-700">{cardData.otherlocation.length - 1}</span> more pickup {cardData.otherlocation.length - 1 === 1 ? 'location' : 'locations'}
            </p>
          )}
          {/* Custom dropdown (not a native <select>) so we can show a green
              partial-payment tick per location AND highlight the selected row as a
              whole section — neither is possible inside a native <option>. */}
          <div className="relative" ref={locRef}>
            <button
              type="button"
              aria-haspopup="listbox"
              aria-expanded={locOpen}
              aria-label="Pickup location"
              onClick={() => setLocOpen((o) => !o)}
              className={`flex items-center justify-between gap-2 bg-gray-50 rounded-card border py-3 pl-3.5 pr-3 w-full text-sm font-medium leading-[1.2] text-left focus:outline-none ${locOpen ? 'border-gray-300' : 'border-gray-200'} ${locationValitn ? '!border-red-500' : ''} ${selectedLocation ? 'text-gray-900' : 'text-gray-400 font-normal'}`}
            >
              <span className="truncate">
                {selectedLocation ? <>{selectedLocation.name} @ <Rupee />{selectedLocation.total?.toFixed(2)}</> : 'Select Pickup Location'}
              </span>
              <svg className={`shrink-0 text-gray-500 transition-transform ${locOpen ? 'rotate-180' : ''}`} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>

            {locOpen && (
              <ul role="listbox" aria-label="Pickup locations" className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-card shadow-card-lift py-1">
                {locationOptions.map((opt) => {
                  const isSel = opt.key === activeLocationKey;
                  return (
                    <li
                      key={opt.key}
                      role="option"
                      aria-selected={isSel}
                      tabIndex={0}
                      onClick={() => { setSelectLocatn(opt.key); setLocationValitn(false); setLocOpen(false); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectLocatn(opt.key); setLocationValitn(false); setLocOpen(false); } }}
                      className={`flex items-center gap-2 px-3 py-2.5 cursor-pointer text-sm ${isSel ? 'bg-redSurface-50 text-primary font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}
                    >
                      {/* green tick = partial payment available here (matches the badge) */}
                      {opt.partialStatus == 1 ? (
                        <svg className="shrink-0 w-4 h-4 text-emerald-600" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
                      ) : (
                        <span className="shrink-0 w-4 h-4" aria-hidden="true" />
                      )}
                      <span className="flex-1 truncate">{opt.name}</span>
                      {/* Road distance sits OUTSIDE the truncating name, and is
                          shrink-0. Inside it, the long branch name ate the whole
                          width and the distance was clipped away with the
                          ellipsis — present in the DOM, invisible on screen.
                          Only rendered when the server actually knows it: null
                          means unknown (no grid for that city, or no drivable
                          route) and shows nothing, never 0 and never a guessed
                          straight line. */}
                      {Number.isFinite(opt.distanceKm) && (
                        <span className="shrink-0 text-xs text-gray-500">{opt.distanceKm} km</span>
                      )}
                      <span className="shrink-0 text-xs opacity-80">@ <Rupee />{opt.total?.toFixed(2)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {locationValitn && (
            <p className="text-xs text-red-500 mt-2">Please select a pickup location</p>
          )}

          {/* Footer: total price (left) + Add to Cart (right), below the dropdown,
              separated by a full-bleed divider (-mx cancels the card padding so the
              line spans edge to edge; px re-aligns the content). */}
          <div className="mt-3 pt-3 -mx-3.5 px-3.5 border-t border-gray-200 flex items-center justify-between gap-3">
            <div className="flex flex-col">
              <div className="flex flex-wrap items-baseline gap-x-1">
                <span className="inline-flex items-center text-lg font-bold text-gray-900 tracking-tight">
                  <Rupee className="mr-[2px]" />{searchedPrice?.toFixed(2)}
                </span>
                {/* Tax note on the rate is mobile-only; on desktop the disclaimer
                    below the filter bar covers it (and it caused price-row wrapping
                    on the narrow 4-col card). */}
                <span className="lg:hidden text-xs font-normal text-gray-500 whitespace-nowrap">(incl. of taxes)</span>
              </div>
              {billedLabel ? (
                <span className="text-xs text-gray-600 leading-snug">
                  {billedLabel.perDay ? (
                    <><Rupee />{billedLabel.perDay} × {billedLabel.days} rental days</>
                  ) : (
                    <>For {billedLabel.days} rental days</>
                  )}
                </span>
              ) : (
                <span className="text-xs text-gray-500 leading-snug">For {formatDuration(cardData.totalDayTime)}</span>
              )}
              <span className="text-xs text-gray-500 leading-snug">{searchedFreeKm} kms included</span>
            </div>
            <button
              disabled={btnDisable}
              onClick={() => handleAddToCart(cardData.vehicle.id)}
              className={`shrink-0 flex bg-primary rounded-pill h-12 px-6 justify-center items-center transition-[background-color,box-shadow] ${
                btnDisable ? 'opacity-70 cursor-not-allowed' : 'hover:bg-primary-dark hover:shadow-cta active:bg-primary-deep'
              }`}
            >
              {btnDisable && vId === cardData.vehicle.id ? (
                <Image
                  src={IMAGES.Spinner2}
                  alt="Loading..."
                  width={30}
                  height={30}
                  className="animate-spin w-[30px] h-[30px]"
                />
              ) : (
                <span className="text-base font-semibold text-white">Add to Cart</span>
              )}
            </button>
          </div>
          <RentalDayNote billing={billedLabel} className="mt-3" />
          {addError && <p role="alert" className="mt-1.5 text-xs font-medium text-red-600 text-right">{addError}</p>}
        </>
      ) : (
        <div className="text-center py-2 text-sm text-gray-500">
          {unavailableMessage}
        </div>
      )}

    </div>
  );
};

export default VehicleCard;
